import { mapPaginatedResponse, unwrapApiData, type ApiClient } from '@klareospace/pharos-lib-front-utils';
import type {
  Import,
  ImportDetail,
  ImportFileDownloadUrl,
  ImportHistoryItem,
  ImportSupportedExtension,
  PaginatedResponse,
  PharosApiResponse,
  PharosPaginatedResponse,
} from '@klareospace/pharos-lib-types';

// ─── Constants ──────────────────────────────────────────────────────────────

const IMPORT_UPLOAD_TIMEOUT_MS = 60_000;

// ─── Types ────────────────────────────────────────────────────────────────────

/** `GET /import` query params — pagination + optional scope filters (PHA-589). No `sort` column is user-selectable yet, so it is intentionally omitted; the backend defaults to `createdAt` desc. */
export interface ListImportsParams {
  page?: number;
  pageSize?: number;
  siteId?: string;
  spaceId?: string;
}

// ─── API ────────────────────────────────────────────────────────────────────

export interface ImportsApi {
  downloadErrorReport: (id: string) => Promise<ImportFileDownloadUrl>;
  downloadImportFile: (id: string) => Promise<ImportFileDownloadUrl>;
  downloadTemplate: (endpoint: string, format: ImportSupportedExtension) => Promise<ImportFileDownloadUrl>;
  getImport: (id: string) => Promise<ImportDetail>;
  importFile: (endpoint: string, file: File) => Promise<Import>;
  listImports: (params: ListImportsParams) => Promise<PaginatedResponse<ImportHistoryItem>>;
}

export function createImportsApi(client: ApiClient): ImportsApi {
  return {
    downloadErrorReport: async (id: string): Promise<ImportFileDownloadUrl> => {
      const response = await client.execute<PharosApiResponse<ImportFileDownloadUrl>>(() =>
        client.instance.get(`/import/${id}/error-report`),
      );
      return unwrapApiData(response, 'downloadErrorReport');
    },

    downloadImportFile: async (id: string): Promise<ImportFileDownloadUrl> => {
      const response = await client.execute<PharosApiResponse<ImportFileDownloadUrl>>(() =>
        client.instance.get(`/import/${id}/file`),
      );
      return unwrapApiData(response, 'downloadImportFile');
    },

    downloadTemplate: async (endpoint: string, format: ImportSupportedExtension): Promise<ImportFileDownloadUrl> => {
      const response = await client.execute<PharosApiResponse<ImportFileDownloadUrl>>(() =>
        client.instance.get(endpoint, { params: { format } }),
      );
      return unwrapApiData(response, 'downloadTemplate');
    },

    getImport: async (id: string): Promise<ImportDetail> => {
      const response = await client.execute<PharosApiResponse<ImportDetail>>(() =>
        client.instance.get(`/import/${id}`),
      );
      return unwrapApiData(response, 'getImport');
    },

    importFile: async (endpoint: string, file: File): Promise<Import> => {
      const formData = new FormData();
      formData.append('file', file);

      // The shared ApiClient sets a default `Content-Type: application/json` on its axios
      // instance, which makes axios silently JSON-serialize a FormData body — override it
      // per-call. The default 10s timeout is also too short for a file upload.
      const response = await client.execute<PharosApiResponse<Import>>(() =>
        client.instance.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: IMPORT_UPLOAD_TIMEOUT_MS,
        }),
      );
      return unwrapApiData(response, 'importFile');
    },

    listImports: async (params: ListImportsParams): Promise<PaginatedResponse<ImportHistoryItem>> => {
      const query = toListQuery(params);
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<ImportHistoryItem>>>(() =>
        client.instance.get('/import', { params: query }),
      );
      return mapPaginatedResponse(unwrapApiData(response, 'listImports'));
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toListQuery(params: ListImportsParams): Record<string, number | string | undefined> {
  return {
    limit: params.pageSize,
    page: params.page,
    siteId: params.siteId,
    spaceId: params.spaceId,
  };
}
